/**
 * Copyright(c) 2024 Magrabi.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @module OmniChannel
 *
 * @author Anokh J Ajai
 */
"use server";

import { AtpCheckResponses, MyAccountOrder, PagedOrderList, RefundData } from "../../common/lib/types";
import OmniChannelClient from "./OmniChannelClient";
import { getMagrabiId } from "../../apiUtils/authServerUtils";
import { myAccountOrderStatusMap } from "../../apiUtils/myAccountUtils";
import { revalidatePath } from "next/cache";
import { commerceConfig } from "../../runtimeConfig/server";

const v2Path = "/external/v2/storefront";
const v3Path = "/external/v3/storefront";
const client = new OmniChannelClient(v2Path);
const publicClient = new OmniChannelClient(v3Path);

export const getOrders = async (
  orderStatusList?: string[],
  channelCode?: string,
  page: number = 0,
  size: number = 5,
  filterCLOrders?: boolean
): Promise<PagedOrderList | undefined> => {
  return client
    .post<PagedOrderList>(
      `/order/search?page=${page}&size=${size}`,
      {
        orderStatusList,
        channelCode,
        contactLensOrder: filterCLOrders,
      },
      "no-store"
    )
    .catch((error) => {
      console.error(error);
      return undefined;
    });
};

export const getOrderDetails = async (orderNo: string, channelCode: string): Promise<MyAccountOrder | undefined> => {
  //TODO : Ask OMS team to parse it from token
  const userId = await getMagrabiId();
  return client
    .post<MyAccountOrder>(
      `/order/user/order-no`,
      {
        userId,
        orderNo,
        channelCode
      },
      "no-store"
    )
    .catch((error) => {
      console.error(error);
      return undefined;
    });
};

export const getOrderTrackingDetails = async (orderNo: string, email: string): Promise<MyAccountOrder | undefined> => {
  return publicClient
    .post<MyAccountOrder>(
      `/order/user/order-no`,
      {
        guestUser: true,
        orderNo,
        email,
      },
      "no-store",
      {},
      {
        "X-APIKEY": commerceConfig.omniChannelApiKey,
      }
    )
    .catch((error) => {
      console.error(error);
      return undefined;
    });
};

export const getRealtimeInventoryDetails = async (productIds: string[], siteId: string): Promise<AtpCheckResponses | undefined> => {
  return publicClient
    .post<AtpCheckResponses>(
      "/order/products-atp-check",
      {
        productIds,
        siteId,
      },
      "no-store",
      {},
      {
        "X-APIKEY": commerceConfig.omniChannelApiKey,
      }
    )
    .catch((error) => {
      console.error(error);
      return undefined;
    });
};

export const initiateReturnOrder = (
  orderId: string,
  reasonId: string,
  reason?: string,
  refundData?: RefundData,
  channelCode?: string
): Promise<MyAccountOrder> => {
  const response = client
    .patch(
      `/order/status`,
      {
        orderId,
        status: myAccountOrderStatusMap.RETURN_INITIATED.key,
        reason,
        reasonId,
        refundData,
        channelCode,
      },
      "no-store",
      {},
      {
        "channel": channelCode ?? ""
      }
    )
    .catch((error) => {
      console.error(error);
      return undefined;
    });
  revalidatePath("/my-account/orders");
  return response as Promise<MyAccountOrder>;
};

export const initiateCancelOrder = (orderId: string, reasonId: string, reason?: string, channelCode?: string): Promise<MyAccountOrder> => {
  const response = client
    .patch(
      `/order/status`,
      {
        orderId,
        status: myAccountOrderStatusMap.CANCELLED.key,
        reason,
        reasonId
      },
      "no-store",
      {},
      {
        "channel": channelCode ?? ""
      }
    )
    .catch((error) => {
      console.error(error);
      return undefined;
    });
  revalidatePath("/my-account/orders");
  return response as Promise<MyAccountOrder>;
};

export const initiateReturnCancelOrder = (orderId: string, reasonId?: string, reason?: string): Promise<MyAccountOrder> => {
  const response = client
    .patch(
      `/order/status`,
      {
        orderId,
        status: myAccountOrderStatusMap.RETURN_CANCEL.key,
        reason,
        reasonId,
      },
      "no-store"
    )
    .catch((error) => {
      console.error(error);
      return undefined;
    });
  revalidatePath("/my-account/orders");
  return response as Promise<MyAccountOrder>;
};
